import React, { useEffect } from 'react';
import { Box, Container, Grid, Typography, Button, Card, CardContent, Avatar, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ShareIcon from '@mui/icons-material/Share';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CommentIcon from '@mui/icons-material/Comment';
import { Helmet } from 'react-helmet-async';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';


export default function GoogleAdsSection() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
  }, []);

  const features = [
    {
      title: "Campaign Strategy",
      description: "Develop targeted ad campaigns that reach your ideal customers at the right moment.",
      icon: "🎯",
      likes: "3.2K",
      comments: "245"
    },
    {
      title: "Keyword Research",
      description: "Find the most valuable keywords that drive qualified traffic to your business.",
      icon: "🔍",
      likes: "2.9K",
      comments: "198"
    },
    {
      title: "Ad Creation",
      description: "Create compelling ad copy and visuals that convert clicks into customers.",
      icon: "✍️",
      likes: "3.4K",
      comments: "256"
    },
    {
      title: "Performance Tracking",
      description: "Monitor and optimize your campaigns for maximum ROI and conversion rates.",
      icon: "📊",
      likes: "2.8K",
      comments: "187"
    }
  ];

  const benefits = [
    "Immediate visibility",
    "Targeted audience reach",
    "Measurable results",
    "Cost-effective advertising",
    "Higher conversion rates",
    "Better ROI tracking"
  ];

  return (
    <>
    
<Box
  sx={{
    position: 'fixed',
    top: { xs: 50, sm: 70 },
    left: { xs: 12, sm: 24 },
    zIndex: 1200,
    bgcolor: 'white',
    borderRadius: '50%',
    boxShadow:5,
    width: { xs: 40, sm: 48 },
    height: { xs: 40, sm: 48 },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}
>
  <IconButton
    onClick={() => navigate(-1)}
    size="medium"
    sx={{
      color: '#1877f2',
      p: 0.5
    }}
  >
    <ArrowBackIosNewIcon fontSize="small" />
  </IconButton>
</Box>
    <Helmet>
  <title>Google Ads Services in Visakhapatnam | SLGT Digital</title>
  <meta
    name="description"
    content="SLGT Digital is a top-rated Google Ads agency in Visakhapatnam. We create effective PPC campaigns that boost visibility, drive targeted traffic, and increase ROI."
  />
  <meta
    name="keywords"
    content="Google Ads Visakhapatnam, PPC Services Vizag, Google AdWords Vizag, Paid Ads Services Andhra Pradesh, SLGT Digital Google Ads"
  />
  <link rel="canonical" href="https://www.slgt.in/services/google-ads" />

  {/* OG Tags */}
  <meta property="og:title" content="Google Ads Services in Visakhapatnam | SLGT Digital" />
  <meta property="og:description" content="Boost your business with high-converting Google Ads campaigns by SLGT Digital." />
  <meta property="og:url" content="https://www.slgt.in/services/google-ads" />
  <meta property="og:type" content="website" />
  <meta property="og:image" content="https://www.slgt.in/assets/services/ga-2.jpg" />

  {/* Twitter Card */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Google Ads Services in Visakhapatnam | SLGT Digital" />
  <meta name="twitter:description" content="Run ROI-focused PPC campaigns with Google Ads experts in Vizag. Connect with SLGT Digital." />
  <meta name="twitter:image" content="https://www.slgt.in/assets/services/ga-2.jpg" />

  {/* JSON-LD Schema */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Google Ads Management",
      "provider": {
        "@type": "Organization",
        "name": "SLGT Digital",
        "url": "https://www.slgt.in",
        "logo": "https://www.slgt.in/logo.png",
        "sameAs": [
              "https://www.instagram.com/slgt_digital_marketing_agency",
          "https://www.facebook.com/slgtdigital"
        ]
      },
      "areaServed": {
        "@type": "Place",
        "name": "Visakhapatnam"
      },
      "url": "https://www.slgt.in/services/google-ads",
      "description": "We create and manage high-performing Google Ads campaigns to grow your leads and sales. SLGT Digital is a trusted Google Ads agency in Visakhapatnam.",
      "image": "https://www.slgt.in/assets/services/ga-2.jpg"
    })}
  </script>
</Helmet>

    <Box sx={{ 
      pt: { xs: 8, sm: 10 },
      pb: { xs: 6, sm: 8 },
      minHeight: '100vh',
      bgcolor: '#f0f2f5'
    }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          bgcolor: '#ffffff',
          color: '#4285f4',
          py: { xs: 6, md: 8 },
          mb: { xs: 4, md: 6 },
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar 
                  sx={{ 
                    width: 56, 
                    height: 56, 
                    mr: 2,
                    bgcolor: '#4285f4'
                  }}
                >
                  G
                </Avatar>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      color: '#4285f4'
                    }}
                  >
                    Google Ads
                  </Typography>
                  <Typography
                    sx={{
                      color: '#1a73e8',
                      fontSize: '0.9rem',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                    }}
                  >
                    Professional PPC Services
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  fontWeight: 700,
                  mb: { xs: 2, md: 3 },
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  color: '#4285f4',
                  lineHeight: 1.2
                }}
              >
                Google Ads Agency in Visakhapatnam.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  mb: { xs: 3, md: 4 },
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  color: '#1a73e8',
                  lineHeight: 1.6
                }}
              >
               <p>Google Ads - Get Seen ,get Clicks, Get Customers!</p>  
                Our Google Ads management services help you reach your target audience effectively and efficiently. We create tailored campaigns that drive traffic and conversions, ensuring you get the most out of your advertising budget.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/requestquotes')}
                sx={{
                  bgcolor: '#4285f4',
                  color: 'white',
                  '&:hover': { bgcolor: '#1a73e8' },
                  px: { xs: 4, sm: 6 },
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 600,
                  borderRadius: '6px',
                  textTransform: 'none'
                }}
              >
                Get Started
              </Button>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <img
                src="/assets/services/ga-2.jpg"
                alt="Google Ads Management Illustration"
                style={{
                  width: '100%',
                  maxWidth: '500px',
                  height: 'auto',
                  display: 'block',
                  margin: '0 auto',
                  borderRadius: '8px'
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ mb: { xs: 6, md: 8 } }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
            fontWeight: 700,
            mb: { xs: 4, md: 6 },
            textAlign: 'center',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            color: '#4285f4',
            lineHeight: 1.2
          }}
        >
          Our Google Ads Services
        </Typography>
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card 
                sx={{ 
                  height: '100%',
                  borderRadius: '8px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  bgcolor: '#ffffff'
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  {/* Post Header */}
                  <Box sx={{ p: 2, borderBottom: '1px solid #e4e6eb' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ width: 40, height: 40, mr: 2, bgcolor: '#4285f4' }}>
                        {feature.icon}
                      </Avatar>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                            color: '#4285f4'
                          }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.9rem',
                            color: '#1a73e8',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                          }}
                        >
                          Professional PPC Service
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Post Content */}
                  <Box sx={{ p: 2 }}>
                    <Typography
                      sx={{
                        color: '#4285f4',
                        fontSize: '1rem',
                        lineHeight: 1.6,
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>

                  {/* Post Actions */}
                  <Box sx={{ 
                    p: 1, 
                    borderTop: '1px solid #e4e6eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton size="small" sx={{ color: '#4285f4' }}>
                        <FavoriteIcon />
                      </IconButton>
                      <Typography sx={{ color: '#1a73e8', fontSize: '0.9rem' }}>
                        {feature.likes}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton size="small" sx={{ color: '#1a73e8' }}>
                        <CommentIcon />
                      </IconButton>
                      <Typography sx={{ color: '#1a73e8', fontSize: '0.9rem' }}>
                        {feature.comments}
                      </Typography>
                    </Box>
                    <IconButton size="small" sx={{ color: '#1a73e8' }}>
                      <ShareIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: '#ffffff', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
              fontWeight: 700,
              mb: { xs: 4, md: 6 },
              textAlign: 'center',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              color: '#4285f4',
              lineHeight: 1.2
            }}
          >
            Benefits of Google Ads
          </Typography>
          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    borderRadius: '8px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    bgcolor: '#ffffff'
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          mr: 2,
                          bgcolor: '#4285f4'
                        }}
                      >
                        ✓
                      </Avatar>
                      <Typography
                        sx={{
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                          fontSize: '1rem',
                          color: '#4285f4',
                          fontWeight: 500
                        }}
                      >
                        {benefit}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ mt: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Card 
          sx={{ 
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            bgcolor: '#ffffff',
            p: { xs: 3, md: 4 }
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
              fontWeight: 700,
              mb: 3,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              color: '#4285f4',
              lineHeight: 1.2
            }}
          >
            Ready to Drive Results with Google Ads?
          </Typography>
          <Typography
            sx={{
              mb: 4,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              maxWidth: '800px',
              mx: 'auto',
              color: '#1a73e8',
              lineHeight: 1.6,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
            }}
          >
            Let's create a powerful Google Ads strategy that helps you reach your target audience and achieve your business goals.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/requestquotes')}
            sx={{
              bgcolor: '#4285f4',
              color: 'white',
              '&:hover': { bgcolor: '#1a73e8' },
              px: { xs: 4, sm: 6 },
              py: { xs: 1.5, sm: 2 },
              fontSize: { xs: '1rem', sm: '1.1rem' },
              fontWeight: 600,
              borderRadius: '6px',
              textTransform: 'none'
            }}
          >
            Request a Quote
          </Button>
        </Card>
      </Container>
    </Box>
  );
  </>

  );
}
